"use client";

import { SearchButtonBase } from "@/components/SearchButtonBase";
import { useSearchTriggerContext } from "@/contexts/SearchTriggerContext";
import { useApplyPendingSearch } from "@/hooks/useApplyPendingSearch";
import pendingSearch from "@/lib/pendingSearch";
import type { MapAreaId } from "@/utils/location";
import type React from "react";

type SearchButtonWithRedirectProps = {
  className?: string;
  /** When true, always shows expanded state with text */
  forceExpanded?: boolean;
  /** Area used when current route has no discover location segment */
  fallbackArea: MapAreaId;
};

/**
 * Search button variant that redirects to homepage on click.
 * Use this on detail pages where search should navigate back to listing.
 */
export function SearchButtonWithRedirect({
  className,
  forceExpanded,
  fallbackArea,
}: SearchButtonWithRedirectProps) {
  const { apply, isPending } = useApplyPendingSearch({
    redirectToHome: true,
    fallbackArea,
  });
  const { setTriggerOpen, setInitialTrigger } = useSearchTriggerContext();

  const handleSearch = (e?: React.MouseEvent | React.KeyboardEvent) => {
    const hasPendingChanges = pendingSearch.hasPendingChanges();

    // If no pending changes, open the activity dropdown instead of searching
    if (!hasPendingChanges) {
      e?.stopPropagation();
      setInitialTrigger("activity");
      setTriggerOpen("activities-dropdown", true);
      return;
    }

    // Otherwise, apply the search as normal
    apply(e);
  };

  return (
    <SearchButtonBase
      className={className}
      onSearch={handleSearch}
      isLoading={isPending}
      alwaysExpanded={forceExpanded}
    />
  );
}
